import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/data/content";
import { RITUAL } from "@/data/ritualConsultation";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";
import { trackEvent } from "@/components/CookieConsent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none placeholder:text-soft/50 focus:border-clay transition-colors duration-300";
const labelCls = "text-xs tracking-[0.25em] uppercase text-soft";

const ScrollButton = ({ label, testid }) => (
  <a
    href="#enquire"
    data-testid={testid}
    className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
  >
    {label}
    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
  </a>
);

const EnquiryForm = ({ f, lang }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", transition: "", ritual_for: "", date_period: "", support_option: "", extra: "", consent: false });
  const [website, setWebsite] = useState(""); // honeypot
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const startedRef = useRef(false);

  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("ritual_consultation_enquiry_started");
    }
  };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name || !form.email || !form.transition || !form.ritual_for || !form.support_option || !form.consent) {
      setError(f.invalid);
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/ritual-enquiries`, {
        ...form,
        phone: form.phone || null,
        date_period: form.date_period || null,
        extra: form.extra || null,
        lang,
        website,
      });
      trackEvent("ritual_consultation_enquiry_submitted");
      setDone(data.intro_call_url);
    } catch {
      setError(f.error);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="ritual-success" className="border-t border-ink/10 pt-8">
        <p className="font-serif text-2xl leading-snug text-ink">{f.success}</p>
        <a
          href={done}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("ritual_intro_call_clicked")}
          data-testid="ritual-schedule-button"
          className="group mt-8 inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
        >
          {f.scheduleCta}
          <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="grid gap-8" data-testid="ritual-enquiry-form">
      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label htmlFor="rc-name" className={labelCls}>{f.name} *</label>
          <input id="rc-name" type="text" value={form.name} onChange={set("name")} onFocus={markStarted} className={inputCls} data-testid="ritual-input-name" autoComplete="name" />
        </div>
        <div>
          <label htmlFor="rc-email" className={labelCls}>{f.email} *</label>
          <input id="rc-email" type="email" value={form.email} onChange={set("email")} onFocus={markStarted} className={inputCls} data-testid="ritual-input-email" autoComplete="email" />
        </div>
      </div>
      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label htmlFor="rc-phone" className={labelCls}>{f.phone}</label>
          <input id="rc-phone" type="tel" value={form.phone} onChange={set("phone")} onFocus={markStarted} className={inputCls} data-testid="ritual-input-phone" autoComplete="tel" />
        </div>
        <div>
          <label htmlFor="rc-date" className={labelCls}>{f.datePeriod}</label>
          <input id="rc-date" type="text" value={form.date_period} onChange={set("date_period")} onFocus={markStarted} className={inputCls} data-testid="ritual-input-date-period" />
        </div>
      </div>
      <div>
        <label htmlFor="rc-transition" className={labelCls}>{f.transition} *</label>
        <textarea id="rc-transition" rows={4} value={form.transition} onChange={set("transition")} onFocus={markStarted} className={`${inputCls} resize-y`} data-testid="ritual-input-transition" />
      </div>
      <div>
        <label htmlFor="rc-for" className={labelCls}>{f.ritualFor} *</label>
        <input id="rc-for" type="text" value={form.ritual_for} onChange={set("ritual_for")} onFocus={markStarted} className={inputCls} data-testid="ritual-input-for" />
      </div>
      <div>
        <label htmlFor="rc-support" className={labelCls}>{f.support} *</label>
        <select id="rc-support" value={form.support_option} onChange={set("support_option")} onFocus={markStarted} data-testid="ritual-input-support"
          className={`${inputCls} cursor-pointer ${form.support_option ? "text-ink" : "text-soft/60"}`}>
          <option value="" disabled>—</option>
          {f.supportOptions.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="rc-extra" className={labelCls}>{f.extra}</label>
        <textarea id="rc-extra" rows={3} value={form.extra} onChange={set("extra")} onFocus={markStarted} className={`${inputCls} resize-y`} data-testid="ritual-input-extra" />
      </div>
      <label className="flex cursor-pointer items-start gap-3" data-testid="ritual-consent-label">
        <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 shrink-0 accent-clay" data-testid="ritual-input-consent" />
        <span className="text-sm leading-relaxed text-soft">{f.consent} *</span>
      </label>
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="ritual-honeypot" />
      </div>
      {error && <p className="text-sm text-rust" role="alert" data-testid="ritual-form-error">{error}</p>}
      <div>
        <button type="submit" disabled={sending} data-testid="ritual-submit-button"
          className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust disabled:opacity-50">
          {sending ? f.sending : f.submit}
          <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
};

export default function RitualConsultationPage() {
  const { lang } = useLang();
  const rc = RITUAL[lang];
  useSeo({ ...rc.seo, image: IMAGES.ritualConsultation });

  useEffect(() => {
    trackEvent("ritual_consultation_page_view");
  }, []);

  return (
    <main data-testid="ritual-consultation-page">
      <PageHero eyebrow={rc.label} title={rc.h1} image={IMAGES.ritualConsultation} tall testid="rc-hero" />

      {/* Opening */}
      <section className="bg-cream py-24 lg:py-32" data-testid="rc-opening">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            {rc.opening.map((p, i) => (
              <p key={i} className={`mt-6 text-base leading-relaxed text-soft first:mt-0 ${i === 0 ? "drop-cap" : ""}`}>{p}</p>
            ))}
            <div className="mt-10"><ScrollButton label={rc.cta} testid="rc-opening-cta" /></div>
          </Reveal>
        </div>
      </section>

      {/* Giving a Transition a Place */}
      <section className="bg-parchment py-24 lg:py-32" data-testid="rc-giving">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{rc.giving.title}</h2>
            {rc.giving.paras.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-soft">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* How We Create the Ritual — featured quotation */}
      <section className="bg-cream py-24 lg:py-32" data-testid="rc-create">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{rc.create.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-12 border-l-2 border-clay/40 pl-6 font-serif italic text-2xl sm:text-3xl leading-snug text-ink" data-testid="rc-quote">
              {rc.create.quote}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            {rc.create.paras.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-soft">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* What Brings People */}
      <section className="bg-parchment py-24 lg:py-32" data-testid="rc-brings">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{rc.brings.title}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft">{rc.brings.intro}</p>
            <ul className="mt-8 grid gap-x-10 gap-y-3 sm:grid-cols-2" data-testid="rc-brings-list">
              {rc.brings.items.map((item) => (
                <li key={item} className="font-serif italic text-lg leading-snug text-ink">{item}</li>
              ))}
            </ul>
            <p className="mt-10 text-base leading-relaxed text-soft">{rc.brings.close}</p>
          </Reveal>
        </div>
      </section>

      {/* How We Begin — numbered steps */}
      <section className="bg-cream py-24 lg:py-32" data-testid="rc-begin">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{rc.begin.title}</h2>
          </Reveal>
          <div className="mt-12 space-y-12">
            {rc.begin.steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="grid gap-4 sm:grid-cols-12" data-testid={`rc-step-${i}`}>
                  <p className="font-serif text-3xl italic text-clay sm:col-span-2">{i + 1}.</p>
                  <div className="sm:col-span-10">
                    <h3 className="font-serif text-xl sm:text-2xl tracking-tight text-ink">{s.title}</h3>
                    {s.paras.map((p, j) => (
                      <p key={j} className="mt-3 text-base leading-relaxed text-soft">{p}</p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ways to Work Together — vertically stacked, no product cards */}
      <section className="bg-parchment py-24 lg:py-32" data-testid="rc-ways">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{rc.ways.title}</h2>
          </Reveal>
          <div className="mt-14 space-y-10">
            {rc.ways.options.map((o) => (
              <Reveal key={o.key}>
                <div
                  className={`border p-8 sm:p-10 ${o.emphasised ? "border-clay/40 bg-cream" : "border-ink/10 bg-parchment"}`}
                  data-testid={`rc-way-${o.key}`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{o.name}</h3>
                    <p className="font-serif text-2xl text-clay" data-testid={`rc-way-price-${o.key}`}>{o.price}</p>
                  </div>
                  {o.paras.map((p, i) => (
                    <p key={i} className="mt-5 text-base leading-relaxed text-soft">{p}</p>
                  ))}
                  <p className="mt-8 text-xs tracking-[0.25em] uppercase text-clay">{o.includesTitle}</p>
                  <ul className="mt-4 space-y-2" data-testid={`rc-way-includes-${o.key}`}>
                    {o.includes.map((inc) => (
                      <li key={inc} className="text-sm leading-relaxed text-soft">{inc}</li>
                    ))}
                  </ul>
                  {o.note && <p className="mt-6 text-sm italic leading-relaxed text-soft/80">{o.note}</p>}
                  {o.note2 && <p className="mt-3 text-sm italic leading-relaxed text-soft/80">{o.note2}</p>}
                  <div className="mt-8"><ScrollButton label={o.cta} testid={`rc-way-cta-${o.key}`} /></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Menarche */}
      <section className="bg-cream py-24 lg:py-32" data-testid="rc-menarche">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{rc.menarche.title}</h2>
            {rc.menarche.paras.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-soft">{p}</p>
            ))}
            <p className="mt-10 text-xs tracking-[0.25em] uppercase text-clay">{rc.menarche.listTitle}</p>
            <ul className="mt-4 space-y-2" data-testid="rc-menarche-list">
              {rc.menarche.items.map((item) => (
                <li key={item} className="text-sm leading-relaxed text-soft">{item}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* A Carefully Held Process */}
      <section className="bg-parchment py-24 lg:py-32" data-testid="rc-care">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{rc.care.title}</h2>
            {rc.care.paras.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-soft">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Begin With a Conversation + enquiry form */}
      <section id="enquire" className="bg-cream py-24 lg:py-32" data-testid="rc-enquire">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{rc.final.title}</h2>
            {rc.final.paras.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-soft">{p}</p>
            ))}
            <h3 className="mt-16 font-serif text-2xl tracking-tight text-ink" data-testid="rc-form-heading">{rc.form.heading}</h3>
            <div className="mt-10">
              <EnquiryForm f={rc.form} lang={lang} />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
